import { parseJwt } from '@packages/toolbox/dist/utils/http-handle';
import { AxiosResponse, isAxiosError } from 'axios';
import { http } from 'SharedDir/backend/axios/http';
import { defineStore } from 'pinia';
import AccessApi from 'SharedDir/backend/api/classes/access.api';
import { AUTHORIZATION } from 'SharedDir/backend/endpoints/access';
import { LoginUserInfo } from 'SharedDir/declarations/access';
import { cookiesStorage } from 'SharedDir/stores/cookiesStorage';
import { useUserStore } from 'SharedDir/stores/user.store';
import { SafeEnv } from 'SharedDir/utils/helpers';

export interface AuthStateInterface {
  token: null | string;
  refreshToken: null | string;
}

function getStorageSettings() {
  if (SafeEnv(() => process.env.AUTH_TOKEN_STORAGE) === 'cookie') {
    return {
      storage: cookiesStorage,
    };
  }
  return true;
}

export const useAuthStore = defineStore('auth', {
  persist: getStorageSettings(),

  state: (): AuthStateInterface => ({
    token: null,
    refreshToken: null,
  }),

  getters: {
    getToken: (state: AuthStateInterface) => state.token,
    getRefreshToken: (state: AuthStateInterface) => state.refreshToken,
    isAuthenticated: (state: AuthStateInterface) => !!state.token,
  },

  actions: {
    setToken(token: string | null) {
      this.token = token;
    },

    setRefreshToken(refreshToken: string | null) {
      this.refreshToken = refreshToken;
    },

    async logout() {
      const userStore = useUserStore();

      if (this.token) {
        try {
          await AccessApi.logout();
        } catch {
          // best-effort: локальный выход важнее серверного
        }
      }

      this.setToken(null);
      this.setRefreshToken(null);
      userStore.clearUser();
    },

    async doRefreshToken() {
      console.debug('Current token: ', this.token);
      console.debug('Current refresh token: ', this.refreshToken);
      const userStore = useUserStore();

      try {
        const {
          data: { token, user },
        } = (await http({
          ...AUTHORIZATION.REFRESH_TOKEN,
          data: { token: this.refreshToken },
        })) as AxiosResponse<LoginUserInfo>;

        this.token = token;

        const parsedRefreshToken = parseJwt(token)?.refresh_token ?? null;
        this.setRefreshToken(parsedRefreshToken);
        userStore.setUser(user);

        return token;
      } catch (error: any) {
        if (isAxiosError(error)) {
          throw error;
        }
        throw new Error(error);
      }
    },
  },
});
